// File: webelieve-frontend/src/features/notes/components/NoteListPanel.tsx
// Left panel with search, filters, and scrollable note card list

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNotesStore } from '../stores/useNotesStore';
import NoteCard from './NoteCard';
import { noteCollaborationSocket } from '../services/noteCollaborationSocket';
import type {
  NoteType,
  NotePriority,
  NoteConfidentiality,
  NoteCasePhase,
} from '../types/note.types';

interface NoteListPanelProps {
  clientId?: string;
  matterId?: string;
  onCreateNote: () => void;
}

const NOTE_TYPE_OPTIONS: { value: NoteType; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'case-strategy', label: 'Case Strategy' },
  { value: 'client-communication', label: 'Client Comm.' },
  { value: 'court-filing', label: 'Court Filing' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'settlement', label: 'Settlement' },
  { value: 'deposition-summary', label: 'Deposition' },
  { value: 'meeting-notes', label: 'Meeting Notes' },
  { value: 'research', label: 'Research' },
  { value: 'intake', label: 'Intake' },
  { value: 'voice-dictation', label: 'Voice Dictation' },
];

const PRIORITY_OPTIONS: { value: NotePriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const CONFIDENTIALITY_OPTIONS: { value: NoteConfidentiality; label: string }[] = [
  { value: 'public', label: 'Public' },
  { value: 'attorney-work-product', label: 'Work Product' },
  { value: 'attorney-client-privilege', label: 'Privileged' },
];

const PHASE_OPTIONS: { value: NoteCasePhase; label: string }[] = [
  { value: 'intake', label: 'Intake' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'motions', label: 'Motions' },
  { value: 'trial-prep', label: 'Trial Prep' },
  { value: 'trial', label: 'Trial' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'closed', label: 'Closed' },
];

const NoteListPanel: React.FC<NoteListPanelProps> = ({ clientId, matterId, onCreateNote }) => {
  const {
    notes,
    selectedNoteId,
    loading,
    total,
    searchQuery,
    filters,
    fetchNotes,
    selectNote,
    searchNotes,
    pinNote,
    deleteNote,
    setFilter,
    setSearchQuery,
    clearFilters,
  } = useNotesStore();

  const [showFilters, setShowFilters] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const realtimeRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const searchQueryRef = useRef(searchQuery);

  // Scope filters for this context
  const scopeFilters = { client_id: clientId, matter_id: matterId };

  useEffect(() => {
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);

  // Load notes on mount and when filters change
  useEffect(() => {
    if (searchQuery.trim()) {
      searchNotes(searchQuery, scopeFilters);
    } else {
      fetchNotes(scopeFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, clientId, matterId]);

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;

    const setupRealtimeListSync = async () => {
      const connected = await noteCollaborationSocket.connect();
      if (!connected || disposed) return;

      unsubscribe = noteCollaborationSocket.on('notes-list-changed', (payload: any) => {
        if (disposed) return;

        if (matterId && payload?.matter_id && payload.matter_id !== matterId) return;
        if (clientId && payload?.client_id && payload.client_id !== clientId) return;

        if (realtimeRefreshTimeoutRef.current) {
          clearTimeout(realtimeRefreshTimeoutRef.current);
        }

        realtimeRefreshTimeoutRef.current = setTimeout(() => {
          if (disposed) return;

          const nextScopeFilters = { client_id: clientId, matter_id: matterId };
          const activeQuery = searchQueryRef.current.trim();

          if (activeQuery) {
            searchNotes(activeQuery, nextScopeFilters);
          } else {
            fetchNotes(nextScopeFilters);
          }
        }, 120);
      });
    };

    setupRealtimeListSync();

    return () => {
      disposed = true;
      if (realtimeRefreshTimeoutRef.current) {
        clearTimeout(realtimeRefreshTimeoutRef.current);
      }
      unsubscribe?.();
    };
  }, [clientId, matterId, fetchNotes, searchNotes]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        if (value.trim()) {
          searchNotes(value, scopeFilters);
        } else {
          fetchNotes(scopeFilters);
        }
      }, 400);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, matterId]
  );

  const handleDelete = useCallback(
    async (noteId: string) => {
      if (window.confirm('Are you sure you want to delete this note?')) {
        await deleteNote(noteId);
      }
    },
    [deleteNote]
  );

  const activeFilterCount = [
    filters.note_type,
    filters.priority,
    filters.confidentiality,
    filters.case_phase,
  ].filter(Boolean).length;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Notes
            {total > 0 && (
              <span className="ml-1.5 text-xs font-normal text-gray-400">({total})</span>
            )}
          </h3>
          <button
            onClick={onCreateNote}
            className="p-1.5 rounded-lg bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast transition-colors"
            title="New Note"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search notes..."
            className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-lex-accent"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`mt-2 flex items-center gap-1 text-xs transition-colors ${
            activeFilterCount > 0
              ? 'text-lex-accent'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
          <svg className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Filter dropdowns */}
        {showFilters && (
          <div className="mt-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <select
                value={filters.note_type || ''}
                onChange={(e) => setFilter('note_type', (e.target.value || undefined) as any)}
                className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Types</option>
                {NOTE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={filters.priority || ''}
                onChange={(e) => setFilter('priority', (e.target.value || undefined) as any)}
                className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Priorities</option>
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={filters.confidentiality || ''}
                onChange={(e) => setFilter('confidentiality', (e.target.value || undefined) as any)}
                className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">Confidentiality</option>
                {CONFIDENTIALITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <select
                value={filters.case_phase || ''}
                onChange={(e) => setFilter('case_phase', (e.target.value || undefined) as any)}
                className="text-xs px-2 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Phases</option>
                {PHASE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="text-xs text-red-500 hover:text-red-600 dark:text-red-400"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Note list */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-lex-accent border-t-transparent rounded-full" />
          </div>
        ) : notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {searchQuery ? 'No notes found' : 'No notes yet'}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
              {searchQuery
                ? 'Try different search terms or filters'
                : 'Create your first note to get started'}
            </p>
            {!searchQuery && (
              <button
                onClick={onCreateNote}
                className="px-3 py-1.5 text-xs bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-lg transition-colors"
              >
                Create Note
              </button>
            )}
          </div>
        ) : (
          notes.map((note) => (
            <NoteCard
              key={note.note_id}
              note={note}
              isSelected={note.note_id === selectedNoteId}
              onSelect={selectNote}
              onPin={pinNote}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default NoteListPanel;
